import styles from "./ShipFromStore.module.scss";
import classNames from "classnames";
import { Alert } from "@JohnLewisPartnership/jl-design-system/dist/components/alert/Alert";

import { ShipFromStoreDrawer } from "./ShipFromStoreDrawer";
import { DeliveryOutlined as Delivery } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/DeliveryOutlined";
import { useRef, useState } from "react";
import { dispatchAnalyticsEvent } from "@/utils";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { DEPRECATED_Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import { TertiaryButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";

import { ChevronRightOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronRightOutlined";

interface Props {
  price: string;
  freeDeliveryThreshold: number;
  hasBuyNowCopyEnabled?: boolean;
}

export function ShipFromStoreUI({
  price,
  freeDeliveryThreshold,
  hasBuyNowCopyEnabled = false,
}: Props) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isStandardDelivery = Number(price) < freeDeliveryThreshold;
  const isDrawerInteractionDispatched = useRef(false);

  function dispatchOverLayOpenedEvent() {
    dispatchAnalyticsEvent("shipfromstore.overlay", {}, {});
  }

  function dispatchMonetateEvent() {
    dispatchMonetateQEvent("standard-pdp-delivery-from-store-sidedrawer-open");
  }

  function handleClick(): void {
    if (!isDrawerInteractionDispatched.current) {
      dispatchOverLayOpenedEvent();
      dispatchMonetateEvent();
      isDrawerInteractionDispatched.current = true;
    }
    setIsDrawerOpen(true);
  }

  const deliveryFromStoreMessage = isStandardDelivery
    ? `Sent directly from one of our stores using £4.50 standard delivery`
    : `Sent directly from one of our stores using free standard delivery`;

  return (
    <>
      <ShipFromStoreUIWithContainer
        deliveryMessage={deliveryFromStoreMessage}
        handleClick={handleClick}
        hasBuyNowCopyEnabled={hasBuyNowCopyEnabled}
        isStandardDelivery={isStandardDelivery}
      />

      <ShipFromStoreDrawer
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        isStandardDelivery={isStandardDelivery}
      />
    </>
  );
}

interface uiProps {
  deliveryMessage: string;
  handleClick: () => void;
  hasBuyNowCopyEnabled: boolean;
  isStandardDelivery: boolean;
}
function ShipFromStoreUIWithContainer({
  deliveryMessage,
  handleClick,
  hasBuyNowCopyEnabled,
  isStandardDelivery,
}: uiProps) {
  return (
    <div className={styles.deliveryMessageContainer}>
      <span className={styles.title}>
        {!hasBuyNowCopyEnabled && (
          <Delivery size="large" className={styles.icon} />
        )}
        Delivery from store
      </span>
      {hasBuyNowCopyEnabled ? (
        <Alert
          type="information"
          variant="white"
          body="Stock of this item is low, but we still have it available in-store. A partner will prepare your order and send it directly to you."
          className={styles.info}
        />
      ) : (
        <DEPRECATED_Message
          type="information"
          body="You can only buy this item on its own. Any other items in your basket will be saved."
          className={styles.info}
        />
      )}

      <ul className={styles.infoList}>
        {hasBuyNowCopyEnabled && (
          <li>
            Buy this item separately, we&#8217;ll save the rest of your basket
            for later
          </li>
        )}
        <li>
          {hasBuyNowCopyEnabled
            ? isStandardDelivery
              ? "Standard delivery £4.50"
              : "Free standard delivery"
            : deliveryMessage}
        </li>
        <li>
          {hasBuyNowCopyEnabled
            ? "Collection not available"
            : "Collection is not available"}
        </li>
        <li>Special offers and promotions may not apply</li>
      </ul>

      <TertiaryButton
        onClick={handleClick}
        className={classNames(
          styles.button,
          hasBuyNowCopyEnabled && styles.buyNowButton,
        )}
        background={"filled"}
        icon={hasBuyNowCopyEnabled ? undefined : ChevronRightOutlined}
        type="button"
        size="small"
        labelText="How delivery from store works"
      />
    </div>
  );
}
